'use client';

import cx from 'classnames';
import { SubmitEvent, ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { logger } from '../../../core/Log/logger';
import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import { TrackingProperties } from '../../../core/Tracking/Amplitude/Amplitude';
import { Anchor } from '../../../design-system/components/anchor/Anchor';
import { Button } from '../../../design-system/components/form/button/Button';
import { Checkbox } from '../../../design-system/components/form/checkbox/Checkbox';
import { Input } from '../../../design-system/components/form/input/Input';
import { Typography } from '../../../design-system/components/typography/Typography';
import { type Destination } from '../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { useUser } from '../../providers/user/useUser';
import { isValidEmail } from '../../utilities/email/isValidEmail';
import { PickDomain } from '../../utilities/types/PickDomain';
import { CguMention } from '../CguMention/CguMention';

import { useNewsletter } from './useNewsletter';

import style from './Newsletter.module.css';

type Props = {
    subscribeMode: number;
    subscribeModeDetail?: string;
    acquisitionTags: string[];
    destination?: PickDomain<Destination, 'slug'>;
    className?: string;
    title?: string;
    description?: string;
    buttonLabel?: string;
    confirmationTitle?: string;
    confirmationDescription?: string;
    noConfirmationDescription?: boolean;
    additionalCheckboxLabel?: ReactNode;
    theme?: 'green';
    afterSubmit?: () => void;
    trackingInformation: {
        subscription: TrackingProperties<typeof ampli.newsletterSubscribed>;
    };
    obfuscateFooterNewsletterLinks?: boolean;
};

interface FormElements extends HTMLFormControlsCollection {
    email?: HTMLInputElement;
    confirmation: HTMLInputElement;
}

interface NewsletterFormElement extends HTMLFormElement {
    readonly elements: FormElements;
}

export function Newsletter({
    subscribeMode,
    acquisitionTags,
    subscribeModeDetail,
    destination,
    className,
    title,
    description,
    buttonLabel,
    confirmationTitle,
    confirmationDescription,
    noConfirmationDescription,
    additionalCheckboxLabel,
    theme,
    afterSubmit,
    trackingInformation,
    obfuscateFooterNewsletterLinks,
}: Props) {
    const { generatePath } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const { user, isUserConnected } = useUser();
    const { send } = useNewsletter();
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const onSubmit = (e: SubmitEvent<NewsletterFormElement>) => {
        e.preventDefault();

        const emailInput = e.currentTarget.elements.email?.value || '';
        const confirmed = e.currentTarget.elements.confirmation.checked;
        const email = user?.email ?? emailInput;

        if (!user && !isValidEmail(emailInput)) {
            setError(
                formatMessage({
                    id: 'global.component.newsletter.error.emailNotValid',
                }),
            );
            return;
        }

        send({
            email,
            subscribeMode,
            acquisitionTags,
            subscribeModeDetail,
            destinationSlug: destination?.slug,
            confirmed,
            trackingInformation,
        })
            .then(() => {
                setSubmitted(true);
                if (afterSubmit) {
                    afterSubmit();
                }
            })
            .catch((error) => {
                setError(
                    formatMessage({
                        id: 'global.component.newsletter.error.retry',
                    }),
                );

                logger.error('Newsletter send failed', {
                    subscribeMode,
                    acquisitionTags,
                    subscribeModeDetail,
                    destinationSlug: destination?.slug,
                    confirmed,
                    error,
                });
            });
    };

    return (
        <form
            onSubmit={onSubmit}
            className={cx(style.form, className, {
                [style.themeGreen]: theme === 'green',
            })}
        >
            {!submitted ? (
                <>
                    <div className={cx('font-l-extrabold', style.title)}>
                        {title ? title : formatMessage({ id: 'global.component.newsletter.title' })}
                    </div>
                    <div className={style.subTitle}>
                        {description
                            ? description
                            : formatMessage({ id: 'global.component.newsletter.subTitle' })}
                    </div>
                    <div className={style.checkboxWrapper}>
                        <Checkbox
                            checkboxProps={{
                                checked: false,
                                name: 'confirmation',
                                required: true,
                            }}
                            label={formatMessage({
                                id: 'global.component.newsletter.genericOptinLabel',
                            })}
                        />
                    </div>
                    {additionalCheckboxLabel ? (
                        <div className={style.checkboxWrapper}>
                            <Checkbox
                                checkboxProps={{
                                    checked: false,
                                    name: 'additionalConfirmation',
                                    required: true,
                                }}
                                label={additionalCheckboxLabel}
                            />
                        </div>
                    ) : null}
                    <div className={style.inputButtonWrapper}>
                        {user === null ? (
                            <Input
                                className={style.input}
                                inputProps={{
                                    type: 'email',
                                    name: 'email',
                                    defaultValue: '',
                                    placeholder: formatMessage({
                                        id: 'global.component.newsletter.inputPlaceholder',
                                    }),
                                    required: true,
                                }}
                                label={formatMessage({
                                    id: 'global.component.newsletter.inputLabel',
                                })}
                                error={error}
                            />
                        ) : null}
                        <Button
                            className={style.button}
                            type="submit"
                            priority="secondary"
                            reversed={theme === 'green'}
                        >
                            {buttonLabel ||
                                formatMessage({
                                    id: 'global.component.newsletter.submitLabel',
                                })}
                        </Button>
                    </div>
                    <Typography className={style.cgu} scale="primary-xs-regular" as="p">
                        {formatMessage(
                            {
                                id: 'global.component.newsletter.cgu.label',
                            },
                            {
                                link: (chunks) => (
                                    <Anchor
                                        obfuscate={obfuscateFooterNewsletterLinks}
                                        href={generatePath('cgu')}
                                        target="_blank"
                                    >
                                        {chunks}
                                    </Anchor>
                                ),
                            },
                        )}
                    </Typography>

                    <div className={style.dataPolicy}>
                        <CguMention
                            isCollapsibleMention={!isUserConnected}
                            isShortMention={isUserConnected}
                            typographyScale="primary-xs-regular"
                            shouldUseDataUsageLabel
                            obfuscateFooterNewsletterLinks={obfuscateFooterNewsletterLinks}
                        />
                    </div>
                </>
            ) : null}
            {submitted ? (
                <div>
                    <div className={cx(style.inscriptionDoneTitle, 'font-l-extrabold')}>
                        {confirmationTitle ||
                            formatMessage({
                                id: 'global.component.newsletter.confirmationTitle',
                            })}
                    </div>
                    {noConfirmationDescription ? null : (
                        <div>
                            {confirmationDescription ||
                                formatMessage({
                                    id: 'global.component.newsletter.confirmationSubTitle',
                                })}
                        </div>
                    )}
                </div>
            ) : null}
        </form>
    );
}
